// src/containers/reportBuilder/ChartsTab.jsx
//
// Charts tab for the admin Report Builder page.
// Ported from takso-poc/takso-reporting-ui/src/App.jsx — the Charts tab.
//
// When no organisation is selected, shows platform-wide data.
// When an organisation is selected, scopes data to that org.
// When activities are selected on the Report Builder tab, Outcomes/Outputs
// charts are further scoped to those program IDs.
//
// Four collapsible top-level sections:
//   1. Activities — status bar (always visible) + Activity mix sub-section
//   2. Outcomes   — outcome frequency ladder
//   3. Outputs    — activity type ladder
//   4. Inputs     — Investment · Venues · Partners
//
// Activity stats (status bar + mix charts) are fetched from the backend so
// they work in both the org-scoped and all-platform views.
// Outcomes/Outputs use ChartHost; empty programIds → all-platform.

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { withTranslation } from 'react-i18next';
import { StackBar } from '../../charts/StackBar';
import { reportBuilderGet } from '../../lib/chartApi';
import ChartHost from '../../components/reports/ChartHost';
import OutcomeRatings from '../../components/reports/OutcomeRatings';
import PostcodeMap from '../../components/reports/PostcodeMap';
import ContractorMap from '../../components/charts/ContractorMap';
import { ActivityOutcomeRatings } from '../../components/charts/ActivityOutcomeRatings';

const fmt = (n) => (Number(n) || 0).toLocaleString();

// ── Section header components ─────────────────────────────────────────────────

function TopSectionHeader({ title, open, onToggle }) {
  return (
    <div
      className="rb-section-header"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onToggle()}
    >
      <h3 className="rb-section-header__title">{title}</h3>
      <span className="rb-section-header__arrow">{open ? '▾' : '▸'}</span>
    </div>
  );
}

function SubSectionHeader({ title, open, onToggle }) {
  return (
    <button className="rb-subsection-header" onClick={onToggle}>
      <span className="rb-subsection-header__arrow">{open ? '▼' : '▶'}</span>
      <span className="rb-subsection-header__title">{title}</span>
    </button>
  );
}

// ── Labelled bar (title + StackBar) ──────────────────────────────────────────

function LabelledBar({ title, parts }) {
  const total = parts.reduce((s, p) => s + (p.n || 0), 0);
  return (
    <div>
      {title && <div className="rb-bar-title">{title}</div>}
      <StackBar parts={parts} total={total} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const slug = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// Truncated slug segment for filenames: slugify then cap at n chars, trim trailing dash
const tslug = (s, n) => slug(s).slice(0, n).replace(/-$/, '');

const SECTION_DEFAULTS = {
  activities: true,
  mix: false,
  completedActivities: true,
  completedMix: false,
  outcomes: true,
  surveys: false,
  evalPlans: true,
  network: false,
  outputs: true,
  inputs: true,
  investment: false,
  venues: false,
  partners: false,
  contractorMap: false,
  postcode: false,
  participants: false,
};
const LS_KEY = 'rb_charts_sections';

function ChartsTab({
  t,
  orgId,
  orgName,
  filteredActivities,
  selectedActivities,
  evaluationPlans = null,
  chartFilters = {},
  chartImages = {},
  useGraded = false,
  refreshToken = 0,
  onIncludedChange,
  onChartSettingsChange,
  onChartImageChange,
}) {
  // "Include in report" state — keyed by chart name
  const [, setIncludedCharts] = useState({});

  const handleIncluded = useCallback(
    (chartKey, value) => {
      setIncludedCharts((prev) => {
        const next = { ...prev, [chartKey]: value };
        if (onIncludedChange) onIncludedChange(next);
        return next;
      });
    },
    [onIncludedChange]
  );

  // Display settings (hideUnused, displayView) — keyed by chart name
  const [, setChartSettings] = useState({});

  const handleSettings = useCallback(
    (chartKey, settings) => {
      setChartSettings((prev) => {
        const next = { ...prev, [chartKey]: settings };
        if (onChartSettingsChange) onChartSettingsChange(next);
        return next;
      });
    },
    [onChartSettingsChange]
  );

  // Section open/closed state — persisted to localStorage so refreshes restore position.
  const [sections, setSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      return { ...SECTION_DEFAULTS, ...(saved || {}) };
    } catch {
      return { ...SECTION_DEFAULTS };
    }
  });
  const toggleSection = (key) => {
    setSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  // Convenience destructure
  const {
    activities: activitiesOpen,
    mix: mixOpen,
    completedActivities: completedActivitiesOpen,
    completedMix: completedMixOpen,
    outcomes: outcomesOpen,
    surveys: surveysOpen,
    evalPlans: evalPlansOpen,
    network: networkOpen,
    outputs: outputsOpen,
    inputs: inputsOpen,
    investment: investmentOpen,
    venues: venuesOpen,
    partners: partnersOpen,
    contractorMap: contractorMapOpen,
    postcode: postcodeOpen,
    participants: participantsOpen,
  } = sections;

  // Rating distribution panels — each entry: { id, title }
  const [ratingOutcomes, setRatingOutcomes] = useState([]);
  const panelRefs = useRef({});
  const expandRefs = useRef({});

  // Auto-create rating panels for all outcomes that have data.
  const handleOutcomeData = useCallback((data) => {
    const outcomes = (data?.outcomes || []).filter(
      (o) => Number(o.usage_count) > 0
    );
    setRatingOutcomes(
      outcomes.map((o) => ({
        id: o.id,
        title: o.title,
        graded: o.graded || null,
      }))
    );
  }, []);

  const onOutcomeBarClick = useCallback((o) => {
    setRatingOutcomes((prev) => {
      const exists = prev.find((r) => r.id === o.id);
      if (exists) {
        // Already exists — expand if collapsed, then scroll to it
        expandRefs.current[o.id]?.expand();
        setTimeout(() => {
          const el = panelRefs.current[o.id];
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 50;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 0);
        return prev;
      }
      // New panel — scroll to it after React renders it
      setTimeout(() => {
        const el = panelRefs.current[o.id];
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 50;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 50);
      return [...prev, { id: o.id, title: o.title, graded: o.graded || null }];
    });
  }, []);

  const closeRatingOutcome = useCallback((id) => {
    setRatingOutcomes((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Activity stats fetched from API
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Same stats, forced to completed (Archived) activities only — powers the
  // "Completed activities" duplicate of the section below. Only single
  // activities have evaluation plans (programs aggregate their linked
  // singles), so this is what shows the real completed-single-activity count.
  const [completedStatsLoading, setCompletedStatsLoading] = useState(true);
  const [completedStats, setCompletedStats] = useState(null);

  // Outcome domain bar data
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [domainParts, setDomainParts] = useState([]);

  // Output type breakdown bar data
  const [outputTypesLoading, setOutputTypesLoading] = useState(true);
  const [outputTypeParts, setOutputTypeParts] = useState([]);

  // Participants subsection data
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [participantTotals, setParticipantTotals] = useState(null);

  // ── Platform filter params (only sent when no org is selected) ────────────
  // Serialised key used as a useEffect dependency so effects re-run on change.

  const filterParams = useMemo(() => {
    const p = {};
    // All 3 selected === no filter; only send when a strict subset is chosen.
    if (chartFilters.statuses?.length > 0 && chartFilters.statuses.length < 3) {
      p.statuses = chartFilters.statuses.join(',');
    }
    if (chartFilters.type && chartFilters.type !== 'all')
      p.type = chartFilters.type;
    if (chartFilters.manager && chartFilters.manager !== 'all')
      p.manager = chartFilters.manager;
    if (chartFilters.startDate) p.startDate = chartFilters.startDate;
    if (chartFilters.endDate) p.endDate = chartFilters.endDate;
    return p;
  }, [chartFilters]);

  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Activity set scoping ───────────────────────────────────────────────────
  // Rule 1: no org → platform-wide (empty scope params → backend uses all data)
  // Rule 2: org selected, no activities → orgId + filterParams
  // Rule 3: activities selected → activityIds (all selected IDs, any type)
  //
  // chartActivityIds: the IDs of all currently selected activities.
  // When empty (rules 1 & 2), scoping falls back to orgId or platform-wide.

  const chartActivityIds = useMemo(
    () => selectedActivities.map((a) => a.id),
    [selectedActivities]
  );

  // scopeParams: merged once here, spread into every API call.
  const scopeParams = useMemo(() => {
    if (chartActivityIds.length > 0) {
      return { activityIds: chartActivityIds.join(',') };
    }
    if (orgId) {
      return { orgId };
    }
    return {};
  }, [chartActivityIds, orgId]);

  // Serialised key for useEffect dependencies (avoids object identity issues).
  const scopeKey = useMemo(() => JSON.stringify(scopeParams), [scopeParams]);

  // ── Fetch activity stats ──────────────────────────────────────────────────
  // Always fetched from the API so linked singles of selected programs are
  // included in counts. scopeParams carries activityIds / orgId / (none).

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStats(null);

    const params = { ...scopeParams, ...filterParams };
    reportBuilderGet('charts/activity-stats', params)
      .then((j) => {
        if (!cancelled && j?.ok) setStats(j);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same fetch, forced to Archived — always completed-only regardless of
  // whatever status the global filter panel has selected, since that's the
  // whole point of this duplicate section.
  useEffect(() => {
    let cancelled = false;
    setCompletedStatsLoading(true);
    setCompletedStats(null);

    const params = {
      ...scopeParams,
      ...filterParams,
      statuses: t('activity_status.archived'),
    };
    reportBuilderGet('charts/activity-stats', params)
      .then((j) => {
        if (!cancelled && j?.ok) setCompletedStats(j);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCompletedStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch outcome domain bar whenever scope changes ───────────────────────

  useEffect(() => {
    let cancelled = false;
    setDomainsLoading(true);
    setDomainParts([]);

    const DOMAIN_ORDER = [
      'Cultural',
      'Social',
      'Economic',
      'Governance',
      'Environmental',
    ];
    const params = { ...scopeParams, ...filterParams };

    reportBuilderGet('charts/outcome-domains', params)
      .then((j) => {
        if (cancelled || !j?.ok) return;
        const domains = Array.isArray(j.domains) ? j.domains : [];
        const parts = DOMAIN_ORDER.map((d) => {
          const hit = domains.find(
            (r) => r.domain?.toLowerCase() === d.toLowerCase()
          );
          return { label: d, n: hit ? hit.count : 0 };
        }).filter((p) => p.n > 0);
        // Append any domains not in the fixed order
        domains.forEach((r) => {
          const d = String(r.domain || t('common.unknown'));
          if (!DOMAIN_ORDER.some((o) => o.toLowerCase() === d.toLowerCase())) {
            parts.push({ label: d, n: r.count });
          }
        });
        setDomainParts(parts);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDomainsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch output type breakdown bar whenever scope changes ────────────────

  useEffect(() => {
    let cancelled = false;
    setOutputTypesLoading(true);
    setOutputTypeParts([]);

    const params = { ...scopeParams, ...filterParams };

    reportBuilderGet('charts/activity-types', params)
      .then((j) => {
        if (cancelled || !j?.ok) return;
        const groups = Array.isArray(j.groups) ? j.groups : [];
        const ORDER = ['Infrastructure', 'Developmental', 'Presentational'];
        const parts = ORDER.map((g) => {
          const hit = groups.find((r) => r.group === g);
          return { label: g, n: hit ? hit.count : 0 };
        }).filter((p) => p.n > 0);
        const tbc = groups.find((r) => r.group === 'Output TBC');
        if (tbc && tbc.count > 0)
          parts.push({ label: 'Output TBC', n: tbc.count });
        setOutputTypeParts(parts);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setOutputTypesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch participant type totals whenever scope changes ──────────────────

  useEffect(() => {
    let cancelled = false;
    setParticipantsLoading(true);
    setParticipantTotals(null);

    const params = { ...scopeParams, ...filterParams };
    reportBuilderGet('charts/participant-types', params)
      .then((j) => {
        if (!cancelled && j?.ok) setParticipantTotals(j.totals);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setParticipantsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derive Activities chart parts from API stats ───────────────────────────

  const statusParts = useMemo(
    () => [
      {
        label: t('activity_status.in_development'),
        n: stats?.statusCounts?.Development || 0,
      },
      {
        label: t('account_status.active'),
        n: stats?.statusCounts?.Active || 0,
      },
      {
        label: t('activity_status.completed'),
        n: stats?.statusCounts?.Completed || 0,
      },
    ],
    [stats]
  );

  const programsVsSingles = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: stats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.singles'),
        n: (stats?.linkedSingles || 0) + (stats?.singlesOnly || 0),
      },
    ],
    [stats]
  );

  const activityMix = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: stats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.linked_singles'),
        n: stats?.linkedSingles || 0,
      },
      {
        label: t('report.builder.activity_types.singles_only'),
        n: stats?.singlesOnly || 0,
      },
    ],
    [stats]
  );

  const ownershipBreakdown = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: stats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.account_holder'),
        n: stats?.linkedSinglesAccountOwner || 0,
      },
      {
        label: t('report.builder.activity_types.guest'),
        n: stats?.linkedSinglesGuest || 0,
      },
      {
        label: t('report.builder.activity_types.singles_only'),
        n: stats?.singlesOnly || 0,
      },
    ],
    [stats]
  );

  // ── Same derivations, completed (Archived) activities only ─────────────────

  const completedProgramsVsSingles = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: completedStats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.singles'),
        n:
          (completedStats?.linkedSingles || 0) +
          (completedStats?.singlesOnly || 0),
      },
    ],
    [completedStats]
  );

  const completedActivityMix = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: completedStats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.linked_singles'),
        n: completedStats?.linkedSingles || 0,
      },
      {
        label: t('report.builder.activity_types.singles_only'),
        n: completedStats?.singlesOnly || 0,
      },
    ],
    [completedStats]
  );

  const completedOwnershipBreakdown = useMemo(
    () => [
      {
        label: t('report.builder.activity_types.programs'),
        n: completedStats?.programs || 0,
      },
      {
        label: t('report.builder.activity_types.account_holder'),
        n: completedStats?.linkedSinglesAccountOwner || 0,
      },
      {
        label: t('report.builder.activity_types.guest'),
        n: completedStats?.linkedSinglesGuest || 0,
      },
      {
        label: t('report.builder.activity_types.singles_only'),
        n: completedStats?.singlesOnly || 0,
      },
    ],
    [completedStats]
  );

  const completedTotalActivities = completedStats?.total || 0;

  const totalActivities = stats?.total || 0;

  // ── Context header scope label ─────────────────────────────────────────────

  const scopeLabel = useMemo(() => {
    if (!orgId) {
      // Platform-wide — derive breakdown from API stats.
      if (statsLoading || !stats) return null;
      const nPrograms = stats.programs || 0;
      const nLinked = stats.linkedSingles || 0;
      const nSingles = stats.singlesOnly || 0;
      const parts = [];
      if (nPrograms > 0)
        parts.push(`${fmt(nPrograms)} program${nPrograms !== 1 ? 's' : ''}`);
      if (nLinked > 0) parts.push(`${fmt(nLinked)} linked`);
      if (nSingles > 0) parts.push(`${fmt(nSingles)} singles`);
      return parts.length > 0 ? parts.join(' · ') : null;
    }

    if (selectedActivities.length === 0) {
      // No explicit selection — derive breakdown from API stats so that linked
      // singles of filtered programs (e.g. Record Type = Program) are included.
      if (statsLoading || !stats) return null;
      const nPrograms = stats.programs || 0;
      const nLinked = stats.linkedSingles || 0;
      const nSingles = stats.singlesOnly || 0;
      const parts = [];
      if (nPrograms > 0)
        parts.push(`${fmt(nPrograms)} program${nPrograms !== 1 ? 's' : ''}`);
      if (nLinked > 0) parts.push(`${fmt(nLinked)} linked`);
      if (nSingles > 0) parts.push(`${fmt(nSingles)} singles`);
      return parts.length > 0
        ? parts.join(' · ')
        : t('empty_message.no_activities');
    }

    // Activities selected — use API stats so linked singles of selected
    // programs are reflected in the label (not just the 2 programs themselves).
    if (statsLoading || !stats) return null;
    const nPrograms = stats.programs || 0;
    const nLinked = stats.linkedSingles || 0;
    const nSingles = stats.singlesOnly || 0;
    const parts = [];
    if (nPrograms > 0)
      parts.push(`${fmt(nPrograms)} program${nPrograms !== 1 ? 's' : ''}`);
    if (nLinked > 0) parts.push(`${fmt(nLinked)} linked`);
    if (nSingles > 0) parts.push(`${fmt(nSingles)} singles`);
    return (
      (parts.length > 0
        ? parts.join(' · ')
        : `${stats.total} ${t('common.selected').toLowerCase()}`) +
      ` (${t('common.selected').toLowerCase()})`
    );
  }, [orgId, selectedActivities, stats, statsLoading]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="rb-charts">
      <div className="rb-charts__context">
        <span className="rb-charts__org">
          {orgName || t('common.takso_platform')}
        </span>
        {scopeLabel && (
          <>
            <span className="rb-charts__sep">·</span>
            <span className="rb-charts__scope">{scopeLabel}</span>
          </>
        )}
      </div>

      <div className="rb-charts__total">
        {statsLoading
          ? t('report.builder.total_activities_loading')
          : t('report.builder.total_activities', {
              value: fmt(totalActivities),
            })}
      </div>

      {/* ── 1. Activities ───────────────────────────────────────────────────── */}
      <div className="rb-charts__section">
        <TopSectionHeader
          title={t('activity_status.all_activities')}
          open={activitiesOpen}
          onToggle={() => toggleSection('activities')}
        />

        {/* Status bar — always visible even when section is collapsed */}
        {statsLoading ? (
          <div className="rb-loading">
            <span>{t('common.loading')}…</span>
          </div>
        ) : (
          <StackBar parts={statusParts} total={totalActivities} />
        )}

        {activitiesOpen && !statsLoading && (
          <div className="rb-charts__subsections">
            <SubSectionHeader
              title={t('report.builder.activity_mix')}
              open={mixOpen}
              onToggle={() => toggleSection('mix')}
            />

            {mixOpen && (
              <div className="rb-charts__mix">
                <LabelledBar
                  title={t('report.builder.completed_programs_vs_singles')}
                  parts={programsVsSingles}
                />
                <LabelledBar
                  title={t('report.builder.completed_activity_mix')}
                  parts={activityMix}
                />
                <LabelledBar
                  title={t('report.builder.completed_ownership_breakdown')}
                  parts={ownershipBreakdown}
                />
                <div className="rb-charts__mix-note">
                  {t('report.builder.total_width_all_activities_description', {
                    orgNameOrAllOrgs: orgName
                      ? ` for ${orgName}`
                      : ' across all organisations',
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── 1b. Completed activities ────────────────────────────────────────── */}
      {/* Same breakdown as above, forced to Archived only — only single      */}
      {/* activities have evaluation plans (programs aggregate their linked   */}
      {/* singles), so "Singles only" + "Linked singles" here is the real     */}
      {/* count of completed activities that can actually have survey data.  */}
      <div className="rb-charts__section">
        <TopSectionHeader
          title={t('activity_status.completed_activities')}
          open={completedActivitiesOpen}
          onToggle={() => toggleSection('completedActivities')}
        />

        {completedStatsLoading ? (
          <div className="rb-loading">
            <span>{t('common.loading')}…</span>
          </div>
        ) : (
          <StackBar
            parts={[{ label: 'Completed', n: completedTotalActivities }]}
            total={completedTotalActivities}
          />
        )}

        {completedActivitiesOpen && !completedStatsLoading && (
          <div className="rb-charts__subsections">
            <SubSectionHeader
              title={t('report.builder.activity_mix')}
              open={completedMixOpen}
              onToggle={() => toggleSection('completedMix')}
            />

            {completedMixOpen && (
              <div className="rb-charts__mix">
                <LabelledBar
                  title={t('report.builder.completed_programs_vs_singles')}
                  parts={completedProgramsVsSingles}
                />
                <LabelledBar
                  title={t('report.builder.completed_activity_mix')}
                  parts={completedActivityMix}
                />
                <LabelledBar
                  title={t('report.builder.completed_ownership_breakdown')}
                  parts={completedOwnershipBreakdown}
                />
                <div className="rb-charts__mix-note">
                  {t(
                    'report.builder.total_width_completed_activities_description',
                    {
                      orgNameOrAllOrgs: orgName
                        ? ` for ${orgName}`
                        : ' across all organisations',
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── 2. Outcomes ─────────────────────────────────────────────────────── */}
      <div className="rb-charts__section">
        <TopSectionHeader
          title={t('common.outcomes')}
          open={outcomesOpen}
          onToggle={() => toggleSection('outcomes')}
        />

        {/* Domain bar — always visible even when section is collapsed */}
        {domainsLoading ? (
          <div className="rb-loading">
            <span>{t('common.loading')}…</span>
          </div>
        ) : (
          <StackBar parts={domainParts} />
        )}

        {outcomesOpen && (
          <div className="rb-charts__subsections">
            <SubSectionHeader
              title={t('report.builder.surveys_condicted')}
              open={surveysOpen}
              onToggle={() => toggleSection('surveys')}
            />
            {surveysOpen && (
              <>
                <div className="rb-charts__section-sub">
                  {t('report.builder.how_often_each_outcome_measure_is_used')}
                </div>
                <ChartHost
                  refreshToken={refreshToken}
                  chart="outcomeFrequency"
                  title={t('report.builder.surveys_condicted')}
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showReportToggle
                  showSavePng
                  onReportToggle={(v) => handleIncluded('outcomeFrequency', v)}
                  onOutcomeBarClick={onOutcomeBarClick}
                  onData={handleOutcomeData}
                />
                <div className="rb-charts__prose">
                  {t('report.builder.outcome_ladder_description')}
                </div>

                {/* Rating distribution panels — one per clicked outcome */}
                {ratingOutcomes.length > 0 && (
                  <div className="rb-rating-panels">
                    {ratingOutcomes.map((ro) => (
                      <div
                        key={ro.id}
                        className="rb-rating-panel"
                        ref={(el) => {
                          panelRefs.current[ro.id] = el;
                        }}
                      >
                        <OutcomeRatings
                          ref={(el) => {
                            expandRefs.current[ro.id] = el;
                          }}
                          outcomeId={ro.id}
                          outcomeName={
                            useGraded && ro.graded ? ro.graded : ro.title
                          }
                          activityIds={chartActivityIds}
                          orgId={orgId}
                          filterParams={filterParams}
                          initialIncluded
                          onClose={() => closeRatingOutcome(ro.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Per evaluation plan charts — always shown when activity selected ── */}
            {selectedActivities.length > 0 && (
              <>
                <SubSectionHeader
                  title={t('activity.evaluation.evaluation_plans')}
                  open={evalPlansOpen}
                  onToggle={() => toggleSection('evalPlans')}
                />
                {evalPlansOpen && (
                  <div className="rb-charts__subsection-body">
                    {!evaluationPlans ? (
                      <div className="rb-charts__section-sub">
                        {t('common.loading')}…
                      </div>
                    ) : evaluationPlans.length === 0 ? (
                      <div className="rb-charts__section-sub">
                        {t('report.builder.no_evaluation_plans')}
                      </div>
                    ) : (
                      evaluationPlans.map((plan) =>
                        (plan.outcomes || []).map((oc) => {
                          const activityName =
                            selectedActivities[0]?.name || t('common.activity');
                          const pngName = `${tslug(activityName, 12)}_${tslug(
                            plan.planName,
                            8
                          )}_${tslug(oc.outcomeTitle, 8)}`;
                          return (
                            <div
                              key={`${plan.planId}-${oc.outcomeId}`}
                              className="rb-eval-plan-chart"
                            >
                              <div className="rb-eval-plan-chart__name">
                                {plan.planName}
                              </div>
                              {/* ActivityOutcomeRatings is rendered directly (not via ChartHost/ChartRegistry)
                                  because it has per-evaluation-plan scoping, a custom PNG capture pipeline,
                                  and an onProse callback that ChartRegistry does not support. */}
                              <ActivityOutcomeRatings
                                activityId={selectedActivities[0]?.id}
                                outcomeId={oc.outcomeId}
                                evaluationPlanIds={[plan.planId]}
                                refreshToken={refreshToken}
                                planName={plan.planName}
                                savePngLabel={`${activityName} · ${plan.planName}`}
                                savePngFilename={pngName}
                                initialIncluded={Boolean(
                                  oc.evaluationId &&
                                  chartImages[oc.evaluationId]
                                )}
                                capturedPng={
                                  oc.evaluationId
                                    ? chartImages[oc.evaluationId] || null
                                    : null
                                }
                                onIncluded={(png) => {
                                  handleIncluded(
                                    `evalPlan-${plan.planId}-${oc.outcomeId}`,
                                    png
                                  );
                                  if (onChartImageChange && oc.evaluationId)
                                    onChartImageChange(oc.evaluationId, png);
                                }}
                                onExcluded={() => {
                                  handleIncluded(
                                    `evalPlan-${plan.planId}-${oc.outcomeId}`,
                                    false
                                  );
                                  if (onChartImageChange && oc.evaluationId)
                                    onChartImageChange(oc.evaluationId, null);
                                }}
                              />
                            </div>
                          );
                        })
                      )
                    )}
                  </div>
                )}
              </>
            )}

            <SubSectionHeader
              title={t('report.builder.connections')}
              open={networkOpen}
              onToggle={() => toggleSection('network')}
            />
            {networkOpen && (
              <>
                <div className="rb-charts__section-sub">
                  {t('report.builder.how_outcome_measures_co_occur')}
                </div>
                <ChartHost
                  refreshToken={refreshToken}
                  chart="outcomeNetwork"
                  title={t('report.builder.connections')}
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showSavePng
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* ── 3. Outputs ──────────────────────────────────────────────────────── */}
      <div className="rb-charts__section">
        <TopSectionHeader
          title={t('report.outputs')}
          open={outputsOpen}
          onToggle={() => toggleSection('outputs')}
        />

        {/* Event-type breakdown bar — always visible even when section is collapsed */}
        {outputTypesLoading ? (
          <div className="rb-loading">
            <span>{t('common.loading')}…</span>
          </div>
        ) : (
          <StackBar parts={outputTypeParts} />
        )}

        {outputsOpen && (
          <>
            <div className="rb-charts__section-sub">
              {t('report.builder.breakdown_of_linked_activity_types')}
            </div>

            <ChartHost
              refreshToken={refreshToken}
              chart="activityTypeLadder"
              activityIds={chartActivityIds}
              orgId={orgId}
              filterParams={filterParams}
              hideUnused
              displayView="grouped"
              showReportToggle
              onReportToggle={(v) => handleIncluded('activityTypeLadder', v)}
              onSettingsChange={(s) => handleSettings('activityTypeLadder', s)}
            />

            {/* ── Participants subsection ── */}
            <SubSectionHeader
              title={t('report.participants')}
              open={participantsOpen}
              onToggle={() => toggleSection('participants')}
            />

            {participantsOpen && (
              <div className="rb-charts__subsection-body">
                {participantsLoading ? (
                  <div className="rb-loading">
                    <span>{t('common.loading')}…</span>
                  </div>
                ) : participantTotals ? (
                  <LabelledBar
                    title={t('report.builder.receptive_vs_active')}
                    parts={[
                      { label: 'Receptive', n: participantTotals.receptive },
                      { label: 'Active', n: participantTotals.active },
                    ].filter((p) => p.n > 0)}
                  />
                ) : null}

                <ChartHost
                  refreshToken={refreshToken}
                  chart="participantTypeLadder"
                  title={t('report.builder.participant_types')}
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showSavePng
                  showReportToggle
                  onReportToggle={(v) =>
                    handleIncluded('participantTypeLadder', v)
                  }
                  onSettingsChange={(s) =>
                    handleSettings('participantTypeLadder', s)
                  }
                />

                <ChartHost
                  refreshToken={refreshToken}
                  chart="participantsByActivityType"
                  title={t('report.builder.participants_by_activity_type')}
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showSavePng
                  showReportToggle
                  onReportToggle={(v) =>
                    handleIncluded('participantsByActivityType', v)
                  }
                  onSettingsChange={(s) =>
                    handleSettings('participantsByActivityType', s)
                  }
                />
              </div>
            )}

            {/* ── Postcode map subsection ── */}
            <SubSectionHeader
              title={t('report.builder.participant_origin_postcode_map')}
              open={postcodeOpen}
              onToggle={() => toggleSection('postcode')}
            />

            {postcodeOpen && selectedActivities.length > 0 && (
              <div className="rb-charts__subsection-body">
                {/* PostcodeMap is rendered directly — Leaflet lifecycle requirements
                    mean it cannot fit the ChartRegistry fetch+render model. */}
                <PostcodeMap
                  projectId={selectedActivities[0].id}
                  showReportToggle
                  onReportToggle={(v) => handleIncluded('postcodeMap', v)}
                />
              </div>
            )}
            {postcodeOpen && selectedActivities.length === 0 && (
              <div className="rb-charts__subsection-body">
                <p style={{ color: '#6b7280', fontSize: 13 }}>
                  {t('report.builder.select_activity_to_view_postcode_map')}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── 4. Inputs ───────────────────────────────────────────────────────── */}
      <div className="rb-charts__section">
        <TopSectionHeader
          title={t('report.inputs')}
          open={inputsOpen}
          onToggle={() => toggleSection('inputs')}
        />

        {inputsOpen && (
          <>
            {/* ── Investment subsection ── */}
            <SubSectionHeader
              title={t('report.builder.investment')}
              open={investmentOpen}
              onToggle={() => toggleSection('investment')}
            />

            {investmentOpen && (
              <div className="rb-charts__subsection-body">
                <ChartHost
                  refreshToken={refreshToken}
                  chart="inputsInvestment"
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showReportToggle
                  onReportToggle={(v) => handleIncluded('inputsInvestment', v)}
                  onSettingsChange={(s) =>
                    handleSettings('inputsInvestment', s)
                  }
                  noteFromData={(d) =>
                    d?.completedCount > 0
                      ? `Based on ${d.completedCount.toLocaleString()} completed ${
                          d.completedCount === 1 ? 'activity' : 'activities'
                        }`
                      : null
                  }
                />
              </div>
            )}
            {/* ── Venues subsection ── */}
            <SubSectionHeader
              title={t('common.venues')}
              open={venuesOpen}
              onToggle={() => toggleSection('venues')}
            />

            {venuesOpen && (
              <div className="rb-charts__subsection-body">
                <ChartHost
                  refreshToken={refreshToken}
                  chart="inputsVenues"
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showReportToggle
                  onReportToggle={(v) => handleIncluded('inputsVenues', v)}
                  onSettingsChange={(s) => handleSettings('inputsVenues', s)}
                />
              </div>
            )}

            {/* ── Partners subsection ── */}
            <SubSectionHeader
              title={t('common.partners')}
              open={partnersOpen}
              onToggle={() => toggleSection('partners')}
            />

            {partnersOpen && (
              <div className="rb-charts__subsection-body">
                <ChartHost
                  refreshToken={refreshToken}
                  chart="inputsPartners"
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showReportToggle
                  onReportToggle={(v) => handleIncluded('inputsPartners', v)}
                  onSettingsChange={(s) => handleSettings('inputsPartners', s)}
                />
              </div>
            )}

            {/* ── Contractor map subsection ── */}
            <SubSectionHeader
              title={t('contractors.contractor_locations')}
              open={contractorMapOpen}
              onToggle={() => toggleSection('contractorMap')}
            />

            {contractorMapOpen && (
              <div className="rb-charts__subsection-body">
                {/* ContractorMap is rendered directly — same justified exception as PostcodeMap. */}
                <ContractorMap
                  activityIds={chartActivityIds}
                  orgId={orgId}
                  filterParams={filterParams}
                  showReportToggle
                  onReportToggle={(v) => handleIncluded('contractorMap', v)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

ChartsTab = withTranslation()(ChartsTab);

export default ChartsTab;
